import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_SUBCOMPANIES_BY_SERVICEID } from "../../graphql/subCompanyQueries";
import { GET_SERVICE_BY_ID } from "../../graphql/serviceQueries";
import Breadcrumb from "../../components/Breadcrumb";
import Card from "../../components/Card";
import {
  EmptyState,
  ErrorBanner,
  ErrorState,
  skeletonTableProps,
} from "../../components/states";
import CompanyDeleteView from "../../components/directory/CompnayDeleteView";
import CompanyView from "./CompanyView";
import SubCompanyForm from "../../components/directory/SubCompanyForm";
import Modal from "../../components/modal/Modal";
import { BsFiletypeCsv } from "react-icons/bs";
import { useToast } from "../../components/Toast/toastHook";
import { Button, Table } from "antd";
import {
  PlusOutlined as PlusIcon,
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
} from "@ant-design/icons";

const Service = () => {
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalCount: 0,
  });

  const { serviceId } = useParams();
  const directoryName = useSearchParams()[0].get("name");
  const [subCompanyListings, setSubCompanyListings] = useState([]);
  const [exportOption, setExportOption] = useState([]);

  const toast = useToast(4000);

  // Company View
  const [companyView, setCompanyView] = useState(false);
  // const [dropDown, setDropDown] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formType, setFormType] = useState("add");

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // For edit
  const [subCompany, setSubCompany] = useState(null);
  const [companyName, setCompanyName] = useState(null);
  const [details, setDetails] = useState(null);
  const [serviceName, setServiceName] = useState("");

  // For view Contacts
  const [subCompanyId, setSubCompanyId] = useState("");

  const { loading, error, data, refetch } = useQuery(GET_SUBCOMPANIES_BY_SERVICEID, {
    refetchQueries: [{ query: GET_SUBCOMPANIES_BY_SERVICEID }],
    variables: {
      serviceId,
      pagination: {
        page: pagination.page,
        limit: pagination.limit,
      },
    },
    fetchPolicy: "no-cache",
  });

  const {  data: exportData, refetch: exportRefetch } = useQuery(GET_SUBCOMPANIES_BY_SERVICEID, {
    refetchQueries: [{ query: GET_SUBCOMPANIES_BY_SERVICEID }],
    variables: {
      serviceId,
    },
    fetchPolicy: "no-cache",
  });

  const { data: serviceData, loading: serviceLoading } = useQuery(
    GET_SERVICE_BY_ID,
    {
      variables: {
        serviceId,
      },
    }
  );

  const onChange = (pagination) => {
    setPagination((prev) => ({
      ...prev,
      page: pagination?.current,
      limit: pagination?.pageSize,
    }));
  };

  useEffect(() => {
    exportRefetch()
    if (data) {
      setSubCompanyListings(data?.getSubCompanyByServiceID?.subCompanies);
      setPagination((prev) => ({
        ...prev,
        totalCount: data?.getSubCompanyByServiceID?.totalCount,
      }));
    }
    if (serviceData) {
      setServiceName(serviceData?.getServiceById?.title);
    }
    if(exportData){
      setExportOption(exportData?.getSubCompanyByServiceID?.subCompanies);
    }
  }, [data, loading, serviceData, exportData]);

  const handleModalRender = () => {
    setFormType("add");
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const handleEdit = (subCompany) => {
    setSubCompany(subCompany);
    setFormType("edit");
    setShowModal(true);
  };

  const handleDeleteModalRender = (id) => {
    setSelectedCompany(id);
    setShowDeleteModal(true);
  };

  const handleShowCompanyView = () => {
    setCompanyView(true);
  };

  const columns = [
    {
      title: "Company Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <span
          className="cursor-pointer hover:underline text-black"
          onClick={() => {
            setSubCompany(record._id);
            setSubCompanyId(record._id);
            setCompanyName(serviceName);
            setDetails(record.details);
            handleShowCompanyView();
          }}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      render: (text) => (
        <span className="font-medium text-black">{text || "No Address"}</span>
      ),
    },
    {
      title: "Phone Number",
      dataIndex: "phone",
      key: "phone",
      render: (text) => (
        <span className="font-medium text-black">
          {text || "No Phone Number"}
        </span>
      ),
    },
    {
      title: "Website",
      dataIndex: "website",
      key: "website",
      render: (text, record) => (
        <span className="font-medium text-black truncate">
          {text || record.url || "No Website"}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex items-center space-x-2">
          <Button
            className="border-secondary text-secondary"
            icon={<PlusIcon />}
            onClick={() => {
              setSubCompany(record._id);
              setSubCompanyId(record._id);
              setCompanyName(serviceName);
              setDetails(record.details);
              handleShowCompanyView();
            }}
          />
          <Button
            className="border-primary text-primary"
            icon={<EditIcon />}
            onClick={() => handleEdit(record)}
          />
          <Button
            danger
            icon={<DeleteIcon />}
            onClick={() => handleDeleteModalRender(record._id)}
          />
        </div>
      ),
    },
  ];

  const COLUMN_MAPPINGS = {
    name: "Company Name",
    address: "Address",
    phone: "Company Phone",
    website: "Company Website",
    serviceAreas: "Service Areas",
    notes: "Notes",
  };

  const handleExportAllColumns = () => {
    exportCompany(Object.keys(COLUMN_MAPPINGS));
  };

  const exportCompany = (checkedColumns) => {
    if (exportOption.length === 0) {
      toast("error", "No company exists.");
      return;
    }

    try {
      const companyCSVData = [];
      exportOption.forEach((subCompany) => {
        const columnsData = {};

        for (const column of checkedColumns) {
          switch (column) {
            case "name":
              columnsData[COLUMN_MAPPINGS[column]] =
                subCompany[column] || "No data";
              break;
            case "address":
              columnsData[COLUMN_MAPPINGS[column]] =
                subCompany[column] || "No data";
              break;
            case "phone":
              columnsData[COLUMN_MAPPINGS[column]] =
                subCompany[column] || "No data";
              break;
            case "website":
              columnsData[COLUMN_MAPPINGS[column]] =
                subCompany[column] || "No data";
              break;
            case "serviceAreas":
              columnsData[COLUMN_MAPPINGS[column]] =
                subCompany[column] || "No data";
              break;
            case "notes":
              columnsData[COLUMN_MAPPINGS[column]] =
                subCompany[column] || "No data";
              break;
            default:
              break;
          }
        }
        companyCSVData.push(columnsData);
      });

      const csvData = companyCSVData.map((user) =>
        Object.values(user)
          .map((value) => JSON.stringify(value))
          .join(",")
      );

      const csvHeaders = Object.keys(companyCSVData[0]);
      csvData.unshift(csvHeaders.join(","));

      const csvContent = csvData.join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "company_data.csv";

      a.click();

      window.URL.revokeObjectURL(url);

      toast("success", "Company data exported successfully.");
    } catch (error) {
      console.log("error in exportCompany", error.message);
      toast("error", "There was an error in exporting company details.");
    }
  };

  const firstLoad = loading && !subCompanyListings?.length;

  return (
    <>
      {/* <CompaniesDropdown
        open={dropDown}
        setData={setSubCompanyListings}
        setOpen={setDropDown}
        directoryId={serviceId}
        data={companyData}
        loading={companyLoading}
      /> */}
      {!companyView && (
        <>
          <Breadcrumb
            pageName="Service List -"
            span={serviceLoading ? "" : serviceName}
          />
          <div className="pb-6">
            <div className="mb-4 flex justify-between items-center flex-wrap gap-6">
              <div className="flex justify-start items-center flex-wrap gap-6">
                <div className="font-medium text-black">Export Companies :</div>
                <div>
                  <BsFiletypeCsv
                    size={40}
                    className="hover:cursor-pointer hover:text-blue"
                    onClick={handleExportAllColumns}
                  />
                </div>
              </div>
            </div>
            <Card>
              <Table
                title={() => (
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-lg">
                      Companies ({pagination?.totalCount || 0})
                    </span>
                    <button
                      onClick={handleModalRender}
                      className="bg-[#B4C540] h-[36px] px-5 text-center rounded-xl text-[#FFFFFF]"
                    >
                      + Add Company
                    </button>
                  </div>
                )}
                bordered
                rowKey={(record) => record._id}
                onChange={onChange}
                {...(firstLoad
                  ? skeletonTableProps(columns)
                  : {
                      columns,
                      dataSource: subCompanyListings,
                      pagination: {
                        position: ["bottomCenter"],
                        current: pagination.page,
                        pageSize: pagination.limit,
                        total: pagination.totalCount,
                      },
                    })}
                locale={{
                  // The Add Company button lives in the table title, so the
                  // table stays mounted on failure and the empty slot carries
                  // the error instead of claiming there is nothing here.
                  emptyText: error ? (
                    <ErrorState onRetry={() => refetch()} />
                  ) : (
                    <EmptyState
                      title="No companies yet"
                      description="Companies added to this service will appear here."
                    />
                  ),
                }}
              />
              {error && !!subCompanyListings?.length && (
                <ErrorBanner onRetry={() => refetch()} />
              )}
            </Card>
          </div>
          <Modal
            title={formType === "add" ? "Add Company" : "Edit Company"}
            showModal={showModal}
            setShowModal={(value) => {
              setShowModal(value);
              // if (!value) setOffice({});
            }}
          >
            <SubCompanyForm
              handleClose={handleClose}
              onDirectoryListAdded={() => {
                refetch();
              }}
              formType={formType}
              subCompany={subCompany}
              serviceId={serviceId}
            />
          </Modal>
          <Modal
            title={"Delete Company"}
            showModal={showDeleteModal}
            setShowModal={(value) => {
              setShowDeleteModal(value);
            }}
          >
            <CompanyDeleteView
              setShowDeleteModal={setShowDeleteModal}
              companyId={selectedCompany}
              handleClose={() => {
                setShowDeleteModal(false);
                refetch();
              }}
            />
          </Modal>
        </>
      )}
      {companyView && (
        <CompanyView
          subCompanyId={subCompanyId}
          companyId={subCompany}
          handleBackClick={() => setCompanyView(false)}
          directoryName={directoryName}
          companyName={companyName}
          details={details}
        />
      )}
    </>
  );
};

export default Service;
